import AddIcon from '@mui/icons-material/Add';
import { Box, Chip, IconButton } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import api from '../../../../services/httpClient';
import { APPLICANT_ENDPOINTS } from '../../../../services/endpoints/rec/applicant.endpoints';
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import { formatDate } from '../../../../utils/date';
import { AsyncMultiAutocomplete, Option } from '../../../common/AsyncMultiAutocomplete';
import Button from '../../../common/Button';
import EditIcon from '../../../common/icons/EditIcon';
import TextField from '../../../common/TextField';
import dayjs from 'dayjs';
import DatePickerField from '../../../common/DatePickerField';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { toText } from '../../../../utils/common';

interface ApplicantProfileExperienceCardProps {
  isEditMode?: boolean;
  totalExperience?: string;
  values: ApplicantProfileData;
  setIsEditMode?: (isEditMode: boolean) => void;
  setValues: (values: ApplicantProfileData) => void;
}

interface ExperienceCardProps {
  index: number;
  values: ApplicantProfileData;
  setIsEditMode?: (isEditMode: boolean) => void;
  setValues: (values: ApplicantProfileData) => void;
  experience: { isEdit?: boolean } & ApplicantProfileData['experiences'][number];
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({
  index,
  experience,
  setIsEditMode,
  values,
  setValues,
}) => {
  const { id } = useParams();
  const { hasPermission } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [technologies, setTechnologies] = useState<{ id: number, name: string }[] | undefined>(experience.technologies);

  // Always hold the latest values so async callbacks don't use a stale closure
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const getTechnologies = async (updateExperience = true) => {
    if (id === undefined) {
      return undefined;
    }
    setIsLoading(true);
    try {
      const res = await api.get<{ skills: { id: number, name: string }[] }>(APPLICANT_ENDPOINTS.companyTech(id, experience.companyId),
        {
          headers: { ...API_HEADERS.APPLICANT_PROFILE_EXPERIENCE_COMPANY_TECH },
        }
      );
      const fetched = res.data.skills || [];
      setTechnologies(fetched);
      if (updateExperience) {
        const latest = valuesRef.current;
        setValues({ ...latest, experiences: latest.experiences.map((ex, i: number) => i === index ? { ...ex, technologies: fetched } : ex) });
      }
      return fetched;
    } catch (err) {
      console.log(err);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }

  const handleLoadTechnologies = async (_query: string): Promise<Option[]> => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.technologies,
      {
        headers: { ...API_HEADERS.VACANCY_SKILL_TECHNOLOGIES_GET },
      }
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  return (
    <Box className="view-sub-section">
      {!experience.isEdit && hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
        <IconButton
          className="view-edit-icon"
          onClick={async () => {
            const loadedTechnologies = technologies ?? await getTechnologies(false);
            const latest = valuesRef.current;
            setValues({
              ...latest,
              experiences: latest.experiences.map((ex, i: number) => i === index ? {
                ...ex,
                isEdit: true,
                ...(loadedTechnologies ? { technologies: loadedTechnologies } : {}),
              } : ex),
            });
            setIsEditMode?.(true);
          }}
        >
          <EditIcon />
        </IconButton>
      )}
      <Box className="view-row-box">
        <Box className="view-column-box">
          <div className="view-label-title">Company Name *</div>
          {experience.isEdit ? <TextField
            className="view-column-edit-box"
            value={toText(experience.company)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, company: e.target.value } : ex) })}
          /> : <div className="view-value-title">{toText(experience.company) || 'N/A'}</div>}
        </Box>
        <Box className="view-column-box">
          <div className="view-label-title">Company URL</div>
          {experience.isEdit ? <TextField
            className="view-column-edit-box"
            value={toText(experience.companyUrl)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, companyUrl: e.target.value } : ex) })}
          /> : <div className="view-value-title">{toText(experience.companyUrl) || 'N/A'}</div>}
        </Box>
        <Box className="view-column-box">
          <div className="view-label-title">Designation *</div>
          {experience.isEdit ? <TextField
            className="view-column-edit-box"
            value={toText(experience.role)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, role: e.target.value } : ex) })}
          /> : <div className="view-value-title">{toText(experience.role) || 'N/A'}</div>}
          <Box className="view-column-box" />
        </Box>
        {/* <Box className="view-column-box" /> */}
      </Box>
      <Box className="view-row-box" sx={{ mt: 2 }}>
        <Box className="view-column-box">
          <div className="view-label-title">Employed Duration</div>
          {experience.isEdit ? (
            <Box className="view-column-edit-box" sx={{ display: 'flex', gap: 1 }}>
              <DatePickerField
                value={experience.from ? dayjs(experience.from) : null}
                onChange={(value: string | null) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, from: value ? value : '' } : ex) })}
              />
              <DatePickerField
                value={experience.to ? dayjs(experience.to) : null}
                onChange={(value: string | null) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, to: value ? value : '' } : ex) })}
              />
            </Box>
          ) : <div className="view-value-title">
              {(() => {
                const from = experience.from ? formatDate(experience.from) : '';
                const to = experience.to ? formatDate(experience.to) : '';
                if (!from && !to) return 'N/A';
                if (!from) return `- ${to}`;
                if (!to) return `${from} -`;
                return `${from} - ${to}`;
              })()}
            </div>}
        </Box>
        <Box className="view-column-box">
          <div className="view-label-title">Years of Experience</div>
          {experience.isEdit ? <TextField
            disabled
            className="view-column-edit-box"
            value={`${experience.experienceYears > 0 ? `${experience.experienceYears} ${experience.experienceYears > 1 ? 'Years ' : 'Year '}` : ''}${experience.experienceMonths > 0 ? `${experience.experienceMonths} ${experience.experienceMonths > 1 ? 'Months' : 'Month'}` : ''}`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, experienceYears: Number(e.target.value) } : ex) })}
          /> : <div className="view-value-title">
            {experience.experienceYears > 0 || experience.experienceMonths > 0 ? (
              <>
                {experience.experienceYears > 0 && `${experience.experienceYears} ${experience.experienceYears > 1 ? 'Years ' : 'Year '}`}
                {experience.experienceMonths > 0 && `${experience.experienceMonths} ${experience.experienceMonths > 1 ? 'Months' : 'Month'}`}
              </>
            ) : 'N/A'}
          </div>}
        </Box>
        <Box className="view-column-box">
          <div className="view-label-title">Technologies</div>
          {!experience.isEdit && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {technologies ?
                technologies.length > 0 ? technologies.map((tech, index) => (
                  <Chip
                    key={index}
                    label={tech.name}
                    title={tech.name}
                    className="view-tag-title"
                  />
                )) : <div className="view-value-title">N/A</div>
                : <Button sx={{ height: '25px' }} variant='contained' onClick={() => getTechnologies()} isLoading={isLoading}>Get Technologies</Button>}
            </Box>
          )}
          {experience.isEdit && (
            <AsyncMultiAutocomplete
              key={technologies ? technologies.map((tech) => tech.id).join(',') : 'empty'}
              multiple
              className="view-column-edit-box"
              value={technologies?.map((tech) => ({ value: tech.id.toString(), label: tech.name })) || []}
              onChange={(value: Option | Option[] | null) => {
                setValues({ ...values, experiences: values.experiences.map((ex, i: number) => i === index ? { ...ex, technologies: value ? (Array.isArray(value) ? value.map((v: Option) => ({ id: Number(v.value), name: v.label })) : [{ id: Number(value.value), name: value.label }]) : [] } : ex) });
              }}
              loadOptions={handleLoadTechnologies}
            />
          )}
        </Box>
        {/* <Box className="view-column-box" /> */}
      </Box>
    </Box>
  );
};

const ApplicantProfileExperienceCard: React.FC<ApplicantProfileExperienceCardProps> = ({
  totalExperience,
  isEditMode,
  setIsEditMode,
  setValues,
  values,
}) => {
  const { hasPermission } = usePermissions();

  return (
    <Box className="view-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <div className="view-section-title">Experience</div>
          {totalExperience ? (
            <>
              <div className="view-section-value-title">.</div>
              <div className="view-section-value-title">
                {totalExperience}
              </div>
            </>
          ) : ''}
        </Box>
        {hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => {
              setIsEditMode?.(true);
              setValues({
                ...values,
                experiences: [...values.experiences, {
                  isEdit: true,
                  company: '',
                  companyUrl: '',
                  role: '',
                  from: '',
                  to: '',
                  experienceYears: 0,
                  technologies: [],
                  companyId: 0,
                  experienceMonths: 0,
                }],
              });
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {values.experiences?.length > 0 ? values.experiences.map((experience, index: number) => (
        <ExperienceCard
          key={index}
          experience={experience}
          setIsEditMode={setIsEditMode}
          setValues={setValues}
          values={values}
          index={index}
        />
      )) : <div className="view-value-title">N/A</div>}
    </Box>
  );
};

export default ApplicantProfileExperienceCard; 